import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { UploadCloud, CheckCircle, Image as ImageIcon, X } from "lucide-react";
import { api, putFileWithProgress, uploadImage } from "../api";
import { useAuth } from "../auth";

const inputCls = "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-crimson/60";
const labelCls = "mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-slate-500";

const ImagePicker = ({ label, file, onPick, required, testId }) => (
  <div>
    <label className={labelCls}>{label}{required ? "" : " (optional)"}</label>
    {file ? (
      <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
        <img src={URL.createObjectURL(file)} alt="" className="h-14 w-14 rounded-lg object-cover" />
        <span className="min-w-0 flex-1 truncate text-sm text-slate-300">{file.name}</span>
        <button type="button" onClick={() => onPick(null)} className="text-slate-500 hover:text-white" data-testid={`${testId}-clear`}><X size={16} /></button>
      </div>
    ) : (
      <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-white/15 bg-white/[0.02] px-4 py-3 text-sm text-slate-400 transition-colors hover:border-crimson/50" data-testid={testId}>
        <ImageIcon size={18} className="text-slate-500" /> Choose image
        <input type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e.target.files?.[0] || null)} />
      </label>
    )}
  </div>
);

const Upload = () => {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initial = params.get("kind");
  const [kind, setKind] = useState(initial === "music" ? "music" : initial === "series" ? "series" : "video");
  const [file, setFile] = useState(null);
  const [thumb, setThumb] = useState(null);
  const [artwork, setArtwork] = useState(null);
  const [cover, setCover] = useState(null);
  const [categories, setCategories] = useState([]);
  const [mySeries, setMySeries] = useState([]);
  const [seriesMode, setSeriesMode] = useState("existing");
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState("idle");
  const [error, setError] = useState("");
  const [publishedId, setPublishedId] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", category: "Film", tags: "", visibility: "public",
    ageRestricted: false, isShort: false,
    artist: "", album: "Single", genre: "Pop", releaseDate: "", lyrics: "", isrc: "",
    seriesId: "", newSeriesTitle: "", newSeriesDescription: "", seasonNumber: "1", episodeNumber: "1",
  });

  useEffect(() => {
    if (ready && !user) navigate("/app/login");
  }, [ready, user, navigate]);

  useEffect(() => {
    api.get("/videos/categories").then((r) => setCategories(Object.keys(r || {}).filter((c) => c !== "All"))).catch(() => {});
  }, []);

  useEffect(() => {
    if (kind === "series") api.get("/series/mine").then((r) => setMySeries(Array.isArray(r) ? r : r.series || [])).catch(() => setMySeries([]));
  }, [kind]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const uploadFile = async (f, purpose) => {
    const init = await api.post("/uploads/initiate", {
      filename: f.name,
      contentType: f.type || (purpose === "media" ? "video/mp4" : "image/jpeg"),
      size: f.size,
      purpose,
      direct: true,
    });
    const fileId = init.fileId || init.id;
    if (init.mode === "mux" && init.uploadUrl) {
      await putFileWithProgress(init.uploadUrl, f, setProgress);
    } else {
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", `${process.env.REACT_APP_MCS_SERVER}/api/uploads/${fileId}/raw`);
        xhr.setRequestHeader("Content-Type", f.type || "application/octet-stream");
        const token = localStorage.getItem("mcs_token");
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.upload.onprogress = (ev) => {
          if (purpose === "media" && ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload = () => (xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.send(f);
      });
    }
    await api.post(`/uploads/${fileId}/complete`, { parts: [] });
    return fileId;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) return setError(kind === "music" ? "Choose an audio file first." : "Choose a video file first.");
    if (!form.title.trim()) return setError("Add a title.");
    if (kind === "music" && !form.artist.trim()) return setError("Add the artist.");
    if (kind === "music" && !artwork) return setError("Add the artwork image.");
    if (kind === "series") {
      if (seriesMode === "existing" && !form.seriesId) return setError("Choose a series.");
      if (seriesMode === "new" && !form.newSeriesTitle.trim()) return setError("Add the new series title.");
    }
    setStage("uploading");
    setProgress(0);
    try {
      const fileId = await uploadFile(file, "media");
      const thumbnailKey = thumb ? await uploadImage(thumb, "image") : undefined;
      setStage("publishing");

      let data;
      if (kind === "music") {
        const artworkKey = await uploadImage(artwork, "image");
        data = {
          title: form.title.trim(), artist: form.artist.trim(), artworkKey,
          album: form.album.trim() || "Single", genre: form.genre.trim() || "Pop",
          visibility: "public",
        };
        if (form.releaseDate) data.releaseDate = form.releaseDate;
        if (form.lyrics.trim()) data.lyrics = form.lyrics;
        if (form.isrc.trim()) data.isrc = form.isrc.trim();
      } else if (kind === "series") {
        let seriesId = form.seriesId;
        if (seriesMode === "new") {
          const coverKey = cover ? await uploadImage(cover, "image") : undefined;
          const s = await api.post("/series", {
            title: form.newSeriesTitle.trim(),
            description: form.newSeriesDescription,
            coverKey,
          });
          seriesId = s.id;
        }
        data = {
          title: form.title.trim(), description: form.description,
          category: form.category || "Film", visibility: form.visibility,
          ageRestricted: form.ageRestricted, seriesId,
          seasonNumber: parseInt(form.seasonNumber, 10) || 1,
          episodeNumber: parseInt(form.episodeNumber, 10) || 1,
        };
        if (thumbnailKey) data.thumbnailKey = thumbnailKey;
      } else {
        data = {
          title: form.title.trim(), description: form.description,
          category: form.category || "Film", visibility: form.visibility,
          ageRestricted: form.ageRestricted,
          tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
          format: form.isShort ? "short" : "video",
          formatOverride: form.isShort ? "short" : undefined,
        };
        if (thumbnailKey) data.thumbnailKey = thumbnailKey;
      }

      await api.post(`/uploads/${fileId}/publish`, { kind: kind === "series" ? "video" : kind, data });
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const st = await api.get(`/uploads/${fileId}/status`);
        if (st.publishedId) {
          setPublishedId(st.publishedId);
          setStage("done");
          return;
        }
      }
      setStage("done");
    } catch (err) {
      setError(err.message);
      setStage("idle");
    }
  };

  if (!user) return null;

  if (stage === "done") {
    return (
      <div className="mx-auto max-w-lg py-16 text-center" data-testid="upload-success">
        <CheckCircle size={44} className="mx-auto text-emerald-400" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-white">Published</h1>
        <p className="mt-2 text-sm text-slate-400">Your {kind === "series" ? "episode" : kind} is live on Michael C Streamline — on the web and in the phone app.</p>
        <div className="mt-6 flex justify-center gap-3">
          {publishedId && kind !== "music" && (
            <Link to={kind === "series" ? `/app/series/${form.seriesId || ""}` : `/app/watch/${publishedId}`} className="rounded-full bg-crimson px-6 py-2.5 font-heading text-xs font-bold text-white" data-testid="view-published-link">
              Watch it
            </Link>
          )}
          <Link to="/app/profile" className="rounded-full border border-white/15 px-6 py-2.5 font-heading text-xs font-bold text-slate-200" data-testid="back-to-profile-link">
            Back to my profile
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl" data-testid="upload-page">
      <h1 className="mb-2 font-heading text-2xl font-bold tracking-tight text-white">Upload</h1>
      <p className="mb-6 text-sm text-slate-500">Uploads go straight to the same library as the phone app.</p>

      <div className="mb-6 grid grid-cols-3 rounded-full border border-white/10 bg-white/[0.03] p-1">
        {[["video", "Video"], ["music", "Music"], ["series", "Series"]].map(([k, label]) => (
          <button key={k} onClick={() => { setKind(k); setError(""); }} data-testid={`upload-kind-${k}`} className={`rounded-full py-2 font-heading text-sm font-semibold transition-colors ${kind === k ? "bg-crimson text-white" : "text-slate-400 hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-4">
        <label className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-white/15 bg-white/[0.02] px-6 py-10 text-center transition-colors hover:border-crimson/50" data-testid="upload-file-drop">
          <UploadCloud size={32} className="text-slate-500" />
          <span className="text-sm text-slate-400">{file ? file.name : kind === "music" ? "Choose an audio file" : "Choose a video file"}</span>
          <input
            type="file"
            accept={kind === "music" ? "audio/*" : "video/*"}
            className="hidden"
            data-testid="upload-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </label>

        <div>
          <label className={labelCls}>Title</label>
          <input value={form.title} onChange={set("title")} placeholder={kind === "series" ? "Episode title" : "Title"} className={inputCls} data-testid="upload-title-input" />
        </div>

        {kind === "video" && (
          <>
            <div>
              <label className={labelCls}>Description</label>
              <textarea value={form.description} onChange={set("description")} rows={3} className={inputCls} data-testid="upload-description-input" />
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select value={form.category} onChange={set("category")} className={inputCls} data-testid="upload-category-select">
                {(categories.length ? categories : ["Film", "Music", "Tech", "Travel", "Vlog", "Art"]).map((c) => (
                  <option key={c} value={c} className="bg-obsidian">{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Tags (comma separated)</label>
              <input value={form.tags} onChange={set("tags")} placeholder="comedy, football" className={inputCls} data-testid="upload-tags-input" />
            </div>
            <div>
              <label className={labelCls}>Visibility</label>
              <select value={form.visibility} onChange={set("visibility")} className={inputCls} data-testid="upload-visibility-select">
                <option value="public" className="bg-obsidian">Public</option>
                <option value="unlisted" className="bg-obsidian">Unlisted</option>
                <option value="private" className="bg-obsidian">Private</option>
              </select>
            </div>
            <ImagePicker label="Thumbnail" file={thumb} onPick={setThumb} testId="upload-thumbnail-picker" />
            <label className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3" data-testid="upload-short-toggle">
              <input type="checkbox" checked={form.isShort} onChange={set("isShort")} className="h-4 w-4 accent-[#E50914]" />
              <span className="text-sm text-slate-300">This is a Short</span>
            </label>
            <label className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3" data-testid="upload-age-toggle">
              <input type="checkbox" checked={form.ageRestricted} onChange={set("ageRestricted")} className="h-4 w-4 accent-[#E50914]" />
              <span className="text-sm text-slate-300">Age-restricted (18+)</span>
            </label>
          </>
        )}

        {kind === "music" && (
          <>
            <div>
              <label className={labelCls}>Artist</label>
              <input value={form.artist} onChange={set("artist")} placeholder="Artist" className={inputCls} data-testid="upload-artist-input" />
            </div>
            <ImagePicker label="Artwork" file={artwork} onPick={setArtwork} required testId="upload-artwork-picker" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Album</label>
                <input value={form.album} onChange={set("album")} className={inputCls} data-testid="upload-album-input" />
              </div>
              <div>
                <label className={labelCls}>Genre</label>
                <input value={form.genre} onChange={set("genre")} className={inputCls} data-testid="upload-genre-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Release date (optional)</label>
                <input type="date" value={form.releaseDate} onChange={set("releaseDate")} className={inputCls} data-testid="upload-release-input" />
              </div>
              <div>
                <label className={labelCls}>ISRC (optional)</label>
                <input value={form.isrc} onChange={set("isrc")} placeholder="GB-XXX-00-00000" className={inputCls} data-testid="upload-isrc-input" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Lyrics (optional)</label>
              <textarea value={form.lyrics} onChange={set("lyrics")} rows={4} className={inputCls} data-testid="upload-lyrics-input" />
            </div>
          </>
        )}

        {kind === "series" && (
          <>
            <div>
              <label className={labelCls}>Series</label>
              <div className="mb-2 grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setSeriesMode("existing")} data-testid="series-mode-existing" className={`rounded-xl border px-4 py-2.5 font-heading text-xs font-bold transition-colors ${seriesMode === "existing" ? "border-crimson bg-crimson/15 text-white" : "border-white/15 text-slate-400"}`}>Existing series</button>
                <button type="button" onClick={() => setSeriesMode("new")} data-testid="series-mode-new" className={`rounded-xl border px-4 py-2.5 font-heading text-xs font-bold transition-colors ${seriesMode === "new" ? "border-crimson bg-crimson/15 text-white" : "border-white/15 text-slate-400"}`}>New series</button>
              </div>
              {seriesMode === "existing" ? (
                <select value={form.seriesId} onChange={set("seriesId")} className={inputCls} data-testid="upload-series-select">
                  <option value="" className="bg-obsidian">Choose a series…</option>
                  {mySeries.map((s) => (
                    <option key={s.id} value={s.id} className="bg-obsidian">{s.title}</option>
                  ))}
                </select>
              ) : (
                <>
                  <input value={form.newSeriesTitle} onChange={set("newSeriesTitle")} placeholder="New series title" className={`${inputCls} mb-2`} data-testid="upload-new-series-title" />
                  <textarea value={form.newSeriesDescription} onChange={set("newSeriesDescription")} placeholder="Series description" rows={2} className={`${inputCls} mb-2`} data-testid="upload-new-series-description" />
                  <ImagePicker label="Series cover" file={cover} onPick={setCover} testId="upload-series-cover-picker" />
                </>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Season</label>
                <input type="number" min="1" value={form.seasonNumber} onChange={set("seasonNumber")} className={inputCls} data-testid="upload-season-input" />
              </div>
              <div>
                <label className={labelCls}>Episode</label>
                <input type="number" min="1" value={form.episodeNumber} onChange={set("episodeNumber")} className={inputCls} data-testid="upload-episode-input" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea value={form.description} onChange={set("description")} rows={2} className={inputCls} data-testid="upload-description-input" />
            </div>
            <ImagePicker label="Episode thumbnail" file={thumb} onPick={setThumb} testId="upload-thumbnail-picker" />
          </>
        )}

        {error && <p className="text-sm text-crimson" data-testid="upload-error">{error}</p>}

        {stage !== "idle" && (
          <div>
            <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
              <div className="h-full bg-crimson transition-[width] duration-300" style={{ width: stage === "uploading" ? `${progress}%` : "100%" }} />
            </div>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-slate-500" data-testid="upload-stage">
              {stage === "uploading" ? `Uploading… ${progress}%` : "Preparing on the server…"}
            </p>
          </div>
        )}

        <button
          disabled={stage !== "idle"}
          className="w-full rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50"
          data-testid="upload-submit-button"
        >
          {stage === "idle" ? `Publish ${kind === "series" ? "episode" : kind}` : "Working…"}
        </button>
      </form>
    </div>
  );
};

export default Upload;
